import { isAdminAtom } from 'r/atoms';
import { isNewUiAtom } from 'r/atoms/newUi';
import { unionContexts } from 'r/utils';
import type { Profile } from 'r/types';
import { ProfileContext } from 'r/contexts';
import AdminHistory from './AdminHistory';
import AnalysisManagerTab from './AnalysisManagerTab';
import ScoreMarketsTab from './ScoreMarketsTab';
import { useAtomValue } from 'jotai';
import AuthorsTab from './AuthorsTab';
import KolsTab from './KolsTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSearchParams } from 'react-router-dom';

export default unionContexts(AdminDashboard, [ProfileContext, 'profile']);

// The new UI (KOL workspace) is opt-in, see isNewUiAtom. The legacy layout keeps
// the Sanitizer tabs active and has no KOLs tab. The SanPlay events tab is
// hidden in both: analyses go to Score markets now.
const TAB_CONFIG = {
  legacy: {
    defaultTab: 'analyses',
    disabledTabs: ['kols', 'events'],
  },
  new: {
    defaultTab: 'kols',
    // Tabs switched off in the UI; direct ?tab= links fall back to the default
    disabledTabs: ['events', 'history', 'authors'],
  },
};

function AdminDashboard({ profile }: { profile: Profile | null }) {
  const isAdmin = useAtomValue(isAdminAtom);
  const isNewUi = useAtomValue(isNewUiAtom);
  const { defaultTab, disabledTabs } = TAB_CONFIG[isNewUi ? 'new' : 'legacy'];
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') ?? defaultTab;
  const activeTab =
    disabledTabs.includes(requestedTab) ? defaultTab : requestedTab;

  if (!isAdmin || !profile) {
    return (
      <div className="py-8 text-center text-secondary">
        {!profile ? 'Please login' : 'You are not admin'}
      </div>
    );
  }

  return (
    <div className="max-w-7xl w-full mx-auto py-10">
      <Tabs
        value={activeTab}
        onValueChange={(tab) => setSearchParams({ tab }, { replace: true })}
        className="w-full"
      >
        <TabsList
          className={`grid w-full ${isNewUi ? 'grid-cols-5' : 'grid-cols-4'}`}
        >
          {isNewUi && <TabsTrigger value="kols">KOLs</TabsTrigger>}
          <TabsTrigger value="analyses">Analyses</TabsTrigger>
          <TabsTrigger value="score-markets">Score markets</TabsTrigger>
          <TabsTrigger value="history" disabled={isNewUi}>
            Analyses (Sanitizer)
          </TabsTrigger>
          <TabsTrigger value="authors" disabled={isNewUi}>
            KOLs / Insights (Sanitizer)
          </TabsTrigger>
        </TabsList>
        {isNewUi && (
          <TabsContent value="kols" className="mt-6">
            <KolsTab />
          </TabsContent>
        )}
        <TabsContent value="analyses" className="mt-6">
          <AnalysisManagerTab />
        </TabsContent>
        <TabsContent value="score-markets" className="mt-6">
          <ScoreMarketsTab />
        </TabsContent>
        <TabsContent value="history" className="mt-6">
          <AdminHistory />
        </TabsContent>
        <TabsContent value="authors" className="mt-6">
          <AuthorsTab profile={profile} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
